/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import remove from 'lodash.remove';
import { action, extendObservable, observable } from 'mobx';
import { createEntityStore } from 'src/luis/core/stores/entityStore';
import { createFeatureStoreHouse } from 'src/luis/core/stores/featureStoreHouse';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A composite entity store factory function.
 *
 * @param id The id of the entity to create.
 * @param entity The composite entity data.
 * @returns A new instance of a composite entity store.
 */
export const createCompositeEntityStore = (id: LuisId, entity: Partial<CompositeEntityStore>): CompositeEntityStore => {
    const entityStore = createEntityStore(id, { ...entity, typeId: ModelType.Composite });

    const compositeEntityStore: SubtractStore<CompositeEntityStore, EntityStore> = {
        children: entity.children || [],
        featureStoreHouse: createFeatureStoreHouse(),

        /**
         * Adds the given entity to the array of children
         * of the entity and returns the new array length.
         *
         * @param child The child to add.
         * @returns The new length of the children array after the
         * child was added.
         */
        addChild(childId) {
            return this.children.push(childId);
        },

        /**
         * Removes the child that has the given id from the
         * array of children of the entity.
         *
         * @param childId The id of the child to delete.
         * @returns True if deletion was successful and
         * false otherwise.
         */
        removeChildById(childIdToRemove) {
            return remove(this.children, childId => childId === childIdToRemove).length > 0;
        }
    };

    return extendObservable(
        entityStore,
        compositeEntityStore,
        {
            children: observable,
            addChild: action,
            removeChildById: action
        },
        { deep: false }
    );
};
